/**
 * The main-thread viewport tile cache (Requirement 1.4).
 *
 * The design puts the whole Workbook in the kernel worker and leaves the main
 * thread "UI state plus a cache of the cells currently visible". This is that
 * cache. Its job is to make a repaint a *synchronous* read: at 60 Hz a frame
 * cannot await a worker round trip, so painting reads whatever the cache holds and
 * missing tiles are requested in the background and painted on a later frame.
 *
 * Four decisions:
 *
 * **Tiles, not viewports.** Requests are aligned to a fixed 64 × 16 grid, so
 * scrolling one row reuses every tile it already has instead of asking for a
 * freshly offset rectangle. Alignment is also what makes invalidation cheap: a
 * `changedRegions` entry maps to the handful of tiles it intersects.
 *
 * **One request per tile in flight.** A fast scroll can cross the same tile
 * boundary several times before the first reply lands. Pending requests are keyed
 * the same way as cached tiles, so the second crossing joins the first request
 * rather than issuing another.
 *
 * **Invalidation is by region, from the kernel's own change set.** The kernel
 * reports `changedRegions` after every mutation; a tile that intersects one is
 * dropped and refetched. Nothing here recomputes what changed, and nothing guesses
 * — a command that touches an unloaded region costs nothing.
 *
 * **Eviction is least-recently-used, by tile count.** A cap on tiles rather than
 * bytes: tiles are uniformly sized by construction, so counting them is the same
 * measurement with none of the accounting.
 */

import { MAX_COLS, MAX_ROWS, type Range, type SheetId } from '../core/types.js';
import { ALIGN_LEFT, DEFAULT_STYLE_INDEX, type ViewTile } from './viewTile.js';

/** Rows per tile. One tile is a little taller than a typical viewport screenful. */
export const TILE_ROWS = 64;

/** Columns per tile. */
export const TILE_COLS = 16;

/**
 * Tiles retained before the least recently used one is evicted.
 *
 * Increased to 360 to keep scroll history in memory for smooth 60-144 Hz navigation.
 */
export const DEFAULT_CACHE_CAPACITY = 360;

/** Where the cache gets tiles. Implemented by `KernelClient.view`. */
export interface ViewSource {
  view(range: Range): Promise<ViewTile>;
}

/** One cell as the painter needs it. */
export interface CellView {
  text: string;
  align: number;
  flags: number;
  /** Index into the owning tile's style table. */
  styleRef: number;
  /** The tile the cell came from, so the painter can read its style table. */
  tile: ViewTile;
}

export interface TileCacheStats {
  tiles: number;
  hits: number;
  misses: number;
  /** Tile requests issued to the worker. */
  requests: number;
  evictions: number;
  invalidations: number;
  pending: number;
}

export interface TileCacheOptions {
  source: ViewSource;
  capacity?: number;
  /** Called when a requested tile arrives, so the caller can schedule a repaint. */
  onTile?: (tile: ViewTile) => void;
  /** Called when a tile request fails. Defaults to swallowing the failure. */
  onError?: (error: unknown, range: Range) => void;
}

export interface TileCache {
  /**
   * Requests every tile covering `range` that is not already cached or in flight.
   * Returns the number of requests issued, so a caller can tell a warm viewport
   * from a cold one.
   */
  ensure(range: Range): number;
  /** Resolves once every tile covering `range` is present. Used by tests and benchmarks. */
  ready(range: Range): Promise<void>;
  /** The cached cell, or `null` when its tile is not loaded yet. */
  cell(sheet: SheetId, row: number, col: number): CellView | null;
  /** Drops every tile intersecting one of `regions` (from `changedRegions`). */
  invalidate(regions: readonly Range[]): number;
  /** Drops every tile of one sheet, or the whole cache when `sheet` is omitted. */
  clear(sheet?: SheetId): void;
  stats(): TileCacheStats;
}

/** Tile coordinates of the tile containing a cell. */
export function tileCoordsOf(row: number, col: number): { tr: number; tc: number } {
  return { tr: Math.floor(row / TILE_ROWS), tc: Math.floor(col / TILE_COLS) };
}

/** The cache key of one tile. Sheet-qualified, so two sheets never collide. */
export function tileKey(sheet: SheetId, tr: number, tc: number): string {
  return `${sheet}|${tr}|${tc}`;
}

/** The sheet rectangle one tile covers. */
export function tileRange(sheet: SheetId, tr: number, tc: number): Range {
  return {
    sheet,
    r0: tr * TILE_ROWS,
    c0: tc * TILE_COLS,
    r1: Math.min(MAX_ROWS - 1, tr * TILE_ROWS + TILE_ROWS - 1),
    c1: Math.min(MAX_COLS - 1, tc * TILE_COLS + TILE_COLS - 1),
  };
}

/** Every tile coordinate covering `range`, row-major. */
export function tilesCovering(range: Range): { tr: number; tc: number }[] {
  const first = tileCoordsOf(Math.min(range.r0, range.r1), Math.min(range.c0, range.c1));
  const last = tileCoordsOf(Math.max(range.r0, range.r1), Math.max(range.c0, range.c1));
  const out: { tr: number; tc: number }[] = [];
  for (let tr = first.tr; tr <= last.tr; tr++) {
    for (let tc = first.tc; tc <= last.tc; tc++) out.push({ tr, tc });
  }
  return out;
}

function intersects(a: Range, b: Range): boolean {
  return (
    a.sheet === b.sheet &&
    Math.min(a.r0, a.r1) <= Math.max(b.r0, b.r1) &&
    Math.min(b.r0, b.r1) <= Math.max(a.r0, a.r1) &&
    Math.min(a.c0, a.c1) <= Math.max(b.c0, b.c1) &&
    Math.min(b.c0, b.c1) <= Math.max(a.c0, a.c1)
  );
}

/** Every tile coordinate covering `range` plus a 1-tile buffer margin for smooth pre-fetching. */
export function paddedTilesCovering(range: Range, padTr = 1, padTc = 1): { tr: number; tc: number }[] {
  const first = tileCoordsOf(Math.min(range.r0, range.r1), Math.min(range.c0, range.c1));
  const last = tileCoordsOf(Math.max(range.r0, range.r1), Math.max(range.c0, range.c1));
  const minTr = Math.max(0, first.tr - padTr);
  const maxTr = Math.min(Math.floor((MAX_ROWS - 1) / TILE_ROWS), last.tr + padTr);
  const minTc = Math.max(0, first.tc - padTc);
  const maxTc = Math.min(Math.floor((MAX_COLS - 1) / TILE_COLS), last.tc + padTc);
  const out: { tr: number; tc: number }[] = [];
  for (let tr = minTr; tr <= maxTr; tr++) {
    for (let tc = minTc; tc <= maxTc; tc++) out.push({ tr, tc });
  }
  return out;
}

export function createTileCache(options: TileCacheOptions): TileCache {
  const capacity = Math.max(1, options.capacity ?? DEFAULT_CACHE_CAPACITY);
  // Insertion order is recency order: a hit deletes and re-sets the entry, so the
  // first key of the map is always the least recently used one.
  const tiles = new Map<string, ViewTile>();
  const pending = new Map<string, Promise<ViewTile>>();
  const stats: TileCacheStats = {
    tiles: 0,
    hits: 0,
    misses: 0,
    requests: 0,
    evictions: 0,
    invalidations: 0,
    pending: 0,
  };

  function touch(key: string, tile: ViewTile): void {
    tiles.delete(key);
    tiles.set(key, tile);
  }

  function evict(): void {
    while (tiles.size > capacity) {
      const oldest = tiles.keys().next();
      if (oldest.done === true) return;
      tiles.delete(oldest.value);
      stats.evictions += 1;
    }
  }

  function request(sheet: SheetId, tr: number, tc: number): Promise<ViewTile> {
    const key = tileKey(sheet, tr, tc);
    const inFlight = pending.get(key);
    if (inFlight !== undefined) return inFlight;
    const range = tileRange(sheet, tr, tc);
    stats.requests += 1;
    const promise = options.source
      .view(range)
      .then((tile) => {
        pending.delete(key);
        tiles.set(key, tile);
        evict();
        options.onTile?.(tile);
        return tile;
      })
      .catch((error: unknown) => {
        pending.delete(key);
        options.onError?.(error, range);
        throw error;
      });
    pending.set(key, promise);
    // A rejected tile request must not surface as an unhandled rejection: callers
    // that only paint never await this promise, and `onError` is the report.
    void promise.catch(() => undefined);
    return promise;
  }

  return {
    ensure(range: Range): number {
      let issued = 0;
      for (const { tr, tc } of tilesCovering(range)) {
        const key = tileKey(range.sheet, tr, tc);
        if (tiles.has(key) || pending.has(key)) continue;
        request(range.sheet, tr, tc);
        issued += 1;
      }
      return issued;
    },

    async ready(range: Range): Promise<void> {
      const waits: Promise<unknown>[] = [];
      for (const { tr, tc } of tilesCovering(range)) {
        const key = tileKey(range.sheet, tr, tc);
        if (tiles.has(key)) continue;
        waits.push(request(range.sheet, tr, tc));
      }
      await Promise.all(waits);
    },

    cell(sheet: SheetId, row: number, col: number): CellView | null {
      const { tr, tc } = tileCoordsOf(row, col);
      const key = tileKey(sheet, tr, tc);
      const tile = tiles.get(key);
      if (tile === undefined) {
        stats.misses += 1;
        return null;
      }
      stats.hits += 1;
      touch(key, tile);
      const at = (row - tile.range.r0) * tile.cols + (col - tile.range.c0);
      if (at < 0 || at >= tile.text.length) {
        return {
          text: '',
          align: ALIGN_LEFT,
          flags: 0,
          styleRef: DEFAULT_STYLE_INDEX,
          tile,
        };
      }
      return {
        text: tile.text[at] as string,
        align: tile.align[at] as number,
        flags: tile.flags[at] as number,
        styleRef: tile.styleRefs[at] as number,
        tile,
      };
    },

    invalidate(regions: readonly Range[]): number {
      if (regions.length === 0) return 0;
      let dropped = 0;
      for (const [key, tile] of [...tiles]) {
        if (regions.some((region) => intersects(region, tile.range))) {
          tiles.delete(key);
          dropped += 1;
        }
      }
      // A tile already in flight would land stale, so its request is abandoned:
      // dropping the pending entry lets the next `ensure` reissue it.
      for (const [key] of [...pending]) {
        const parts = key.split('|');
        const tr = Number(parts[parts.length - 2]);
        const tc = Number(parts[parts.length - 1]);
        const sheet = parts.slice(0, -2).join('|');
        if (regions.some((region) => intersects(region, tileRange(sheet, tr, tc)))) {
          pending.delete(key);
        }
      }
      stats.invalidations += dropped;
      return dropped;
    },

    clear(sheet?: SheetId): void {
      if (sheet === undefined) {
        tiles.clear();
        pending.clear();
        return;
      }
      for (const [key, tile] of [...tiles]) {
        if (tile.range.sheet === sheet) tiles.delete(key);
      }
      for (const key of [...pending.keys()]) {
        if (key.startsWith(`${sheet}|`)) pending.delete(key);
      }
    },

    stats(): TileCacheStats {
      return { ...stats, tiles: tiles.size, pending: pending.size };
    },
  };
}
